import { useEffect } from "react";
import { useAtom, useSetAtom } from "jotai";
import { CirclePlus, ChevronRight } from "lucide-react";
import { Card, CardContent, CardFooter } from "@design-lab/shared/ui/card";
import { Button } from "@design-lab/shared/ui";
import { ArtworkList } from "@design-lab/shared/ui/ArtworkList";
import {
  transfersManagerAtom,
  transfersManagerMachine
} from "@design-lab/entities/transfers-lab/model/transfers-manager.machine";
import { MAX_TRANSFER_ARTWORKS, uploadModalOpenAtom } from "@design-lab/entities/transfers-lab";
import { ReviewStatusMessage } from "./ReviewStatusMessage";
import { TransferQualitySection } from "./TransferQualitySection";

export function TransfersImageReview() {
  const [state, send] = useAtom(transfersManagerAtom);
  const { items, selectedId } = state.context;
  const nextTabInfo = transfersManagerMachine.selectors.nextTabInfo(state);
  const setUploadModalOpen = useSetAtom(uploadModalOpenAtom);

  // Get the selected artwork actor
  const selectedArtwork = selectedId ? items.find(item => item.id === selectedId) : null;
  const selectedFormControl = selectedArtwork?.getSnapshot().context.formControl;

  // Auto-mark selected artwork as reviewed when component mounts or selectedId changes
  useEffect(() => {
    if (!selectedId) return;
    const selectedItem = items.find(i => i.id === selectedId);
    if (!selectedItem) return;
    const snapshot = selectedItem.getSnapshot();
    // Only mark as reviewed if not already reviewed
    if (!snapshot.context.isReviewed) {
      selectedItem.send({ type: "review.mark" });
      // Notify parent machine that review state has changed
      send({ type: "review.state.changed" });
    }
  }, [selectedId, items, send]);

  const handleSelect = (id: string) => {
    // Mark as reviewed when selected
    const item = items.find(i => i.id === id);
    if (item) {
      const snapshot = item.getSnapshot();
      if (!snapshot.context.isReviewed) {
        item.send({ type: "review.mark" });
        // Notify parent machine that review state has changed
        send({ type: "review.state.changed" });
      }
    }
    send({ type: "artwork.select", id });
  };

  // Count reviewed items
  const reviewedCount = items.filter(item => {
    const snapshot = item.getSnapshot();
    return snapshot.context.isReviewed;
  }).length;

  const handleAddMoreClick = () => {
    // Switch to upload tab and show modal
    send({ type: "tab.change", tab: "upload" });
    // Use setTimeout to ensure tab switch happens first
    setTimeout(() => {
      setUploadModalOpen(true);
    }, 100);
  };

  return (
    <Card className="flex h-full flex-1 flex-col border-none shadow-none lg:border lg:shadow">
      <CardContent className="flex flex-1 flex-col overflow-y-auto pt-6">
        <div className="flex flex-1 flex-col gap-2">
          <div className="rounded-[14px] bg-white">
            {/* Artwork grid at the top */}
            <div className="flex flex-wrap gap-3">
              <ArtworkList items={items} selectedId={selectedId} onSelect={handleSelect} />

              {/* Empty slots with plus icons - show up to MAX_TRANSFER_ARTWORKS total */}
              {Array.from({ length: Math.max(0, MAX_TRANSFER_ARTWORKS - items.length) }).map(
                (_, i) => (
                  <button
                    key={`empty-${i}`}
                    type="button"
                    onClick={handleAddMoreClick}
                    className="group relative flex h-[68px] w-[68px] cursor-pointer items-center justify-center rounded-lg border border-dashed border-blue-600 bg-white transition-colors hover:bg-blue-50"
                    data-testid={`add-artwork-slot-${i}`}
                  >
                    <CirclePlus className="size-8 text-blue-600" />
                  </button>
                )
              )}
            </div>

            {/* Status message below the grid */}
            <div className="mt-6">
              <ReviewStatusMessage totalImages={items.length} reviewedCount={reviewedCount} />
            </div>

            {/* Quality tabs section */}
            {selectedFormControl && (
              <TransferQualitySection key={selectedId} formControl={selectedFormControl} />
            )}
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex items-center justify-between gap-2 pt-6">
        <div className="flex items-center gap-2">
          <Button
            data-testid="next-button"
            variant="destructive"
            size="xl"
            isDisabled={reviewedCount < items.length || !nextTabInfo.enabled}
            onPress={() => send({ type: "tab.next" })}
            className="text-background min-h-12 w-fit bg-blue-700 hover:bg-blue-800"
          >
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
